import {
  Autocomplete,
  type AutocompleteRenderValueGetItemProps,
  CircularProgress,
  Stack,
  TextField,
} from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { MomentFormMoment } from "../../../moment/types/moment-form-moment";
import { useChannels } from "../../../recipients/hooks/use-channels";
import type { RecipientPersonGroup } from "../../../recipients/server/supabase-recipient-service";
import { ChannelChip } from "../channel-chip";
import { ChannelTarget } from "../channel-target";
import { TeamsChannelNotify } from "../teams-channel-notify";
import type { ToFormProps } from "./types";

type ChannelFormProps = ToFormProps & {
  showTargetOptions?: boolean;
};

type MinimiumRecipientPerson = Pick<
  RecipientPersonGroup,
  "id" | "group" | "display" | "postability" | "destination"
>;

export const ChannelForm: FC<ChannelFormProps> = ({
  formDisabled,
  showTargetOptions = true,
  ...rest
}) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();
  const [inputValue, setInputValue] = useState("");

  useWatch({
    control,
    name: ["channel", "additional_recipients"],
  });
  const [service, additionalRecipients] = getValues(["channel", "additional_recipients"]);

  const [value, setValue] = useState<{
    valid: MinimiumRecipientPerson[];
    invalid: MinimiumRecipientPerson[];
  }>({ valid: [], invalid: [] });

  const [errorMessage, setErrorMessage] = useState<string | undefined>("");

  const renderChips = useCallback(
    (
      channels: MinimiumRecipientPerson[],
      getItemProps: AutocompleteRenderValueGetItemProps<true>,
    ): React.JSX.Element[] =>
      channels.map((channel, index) => (
        <ChannelChip
          key={channel.id}
          channel={channel}
          index={index}
          service={service}
          getTagProps={getItemProps}
        />
      )),
    [service],
  );

  const { channels, loading, check } = useChannels(service);

  useEffect(() => {
    const selectedChannels = additionalRecipients
      .map((id) => channels.find((channel) => channel.id === id))
      .filter(isChannel);

    void check(selectedChannels).catch(console.error);

    const deletedSelectedChannels = additionalRecipients
      .filter((id) => !channels.find((channel) => channel.id === id))
      .map(
        (id) =>
          ({
            id,
            postability: false,
            display: "Deleted Channel",
          }) as RecipientPersonGroup,
      );

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue({ valid: selectedChannels, invalid: deletedSelectedChannels });
    setErrorMessage(undefined);
  }, [additionalRecipients, channels, check, setValue]);

  useEffect(() => {
    if (value.invalid.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setErrorMessage("Some channels are invalid, please remove them before continuing");
      return;
    }

    const channel = [...value.valid].reverse().find((c) => c.postability === false);
    channel &&
      setErrorMessage(
        `#${channel.display || channel.destination} does not have the correct posting permissions`,
      );
  }, [value, setErrorMessage]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInputValue("");
  }, [additionalRecipients]);

  const optionLabel = useCallback((r: MinimiumRecipientPerson): string => {
    return r.display ?? r.destination;
  }, []);

  const label = useMemo(() => {
    switch (service) {
      case "slack":
        return "Slack Channels";
      case "teams":
        return "Teams Channels";
      default:
        return "Channels";
    }
  }, [service]);
  return (
    <Stack spacing={2} direction="column">
      <Controller
        name="additional_recipients"
        control={control}
        render={({ field }) => (
          <Autocomplete
            value={[...value.invalid, ...value.valid]}
            multiple
            disablePortal
            id="additional-recipients-select"
            options={channels}
            groupBy={(r) => r.group || ""}
            getOptionLabel={optionLabel}
            getOptionKey={(c) => c.id}
            loading={loading}
            disabled={formDisabled}
            sx={{ minWidth: 200, pt: 2 }}
            onChange={(event, newValues) => {
              field.onChange(newValues.map((v) => v.id));
            }}
            onInputChange={(event, newInputValue, reason) => {
              // ignore blur & reset changes. we'll handle that in useEffect
              if (reason === "input") {
                setInputValue(newInputValue);
              }
            }}
            renderValue={renderChips}
            inputValue={inputValue}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={label}
                error={Boolean(errorMessage)}
                helperText={errorMessage}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    "data-testid": "additional-recipients-input",
                  },
                }}
              />
            )}
          />
        )}
      />
      {showTargetOptions && service === "teams" && (
        <TeamsChannelNotify formDisabled={formDisabled} {...rest} />
      )}
      {showTargetOptions && <ChannelTarget formDisabled={formDisabled} {...rest} />}
    </Stack>
  );
};

const isChannel = (c: RecipientPersonGroup | undefined): c is RecipientPersonGroup => {
  return c !== undefined;
};
